import { FormEvent, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { trackOrder } from '../utils/apiHelpers';
import {
  Search,
  Truck,
  Package,
  ShoppingBag,
  ClipboardList,
  Mail,
  Phone,
} from 'lucide-react';

// ─── helpers ────────────────────────────────────────────────────────────────

const formatCurrency = (value?: string | number | null) => {
  if (value === null || value === undefined) return 'Rs 0.00';
  const numeric =
    typeof value === 'number' ? value : parseFloat(String(value) || '0');
  if (Number.isNaN(numeric)) return 'Rs 0.00';
  return `Rs ${numeric.toLocaleString('en-PK', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const buildSpecLabel = (spec?: Record<string, any>) => {
  if (!spec || typeof spec !== 'object') return '';
  return Object.entries(spec)
    .filter(([key, value]) => key !== 'selection' && Boolean(value))
    .map(([key, value]) => {
      const label = key.replace(/_/g, ' ');
      return `${label.charAt(0).toUpperCase() + label.slice(1)}: ${value}`;
    })
    .join(', ');
};

const parseAmount = (value?: string | number | null) => {
  if (typeof value === 'number') return value;
  const numeric = parseFloat(value || '0');
  return Number.isNaN(numeric) ? 0 : numeric;
};

function orderDateFormatter(dateString?: string) {
  if (!dateString) return '--';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

// ─── component ──────────────────────────────────────────────────────────────

export default function TrackOrderPage() {
  const [email, setEmail] = useState('');
  const [order, setOrder] = useState<any | null>(null);
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ── derived customer info ──────────────────────────────────────────────────

  const customerEmail = useMemo(
    () =>
      order?.customer_details?.user_details?.email ||
      order?.customer_details?.email ||
      order?.guest_email ||
      'Not provided',
    [order]
  );

  const customerPhone = useMemo(
    () =>
      order?.customer_details?.phone?.number ||
      order?.guest_phone ||
      'Not provided',
    [order]
  );

  // ── order items & totals ───────────────────────────────────────────────────

  const orderItems: any[] = Array.isArray(order?.items)
    ? order?.items ?? []
    : Array.isArray(order?.order_items)
    ? order?.order_items ?? []
    : [];

  const subtotal =
    orderItems.length > 0
      ? orderItems.reduce(
          (sum, item) => sum + parseAmount(item.price) * (item.quantity || 1),
          0
        )
      : parseAmount(order?.total_amount);

  const total = parseAmount(order?.total_amount) || subtotal;

  // ── fetch by email ─────────────────────────────────────────────────────────

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    if (!email.trim()) {
      setError('Please enter your email address.');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setOrder(null);
      setOrders([]);

      const result = await trackOrder({
        email: email.trim(),
      });

      if (result.orders && Array.isArray(result.orders) && result.orders.length > 0) {
        setOrders(result.orders);
        setOrder(result.orders[0]);
      } else if (result.order) {
        setOrder(result.order);
        setOrders([result.order]);
      } else {
        throw new Error('No orders found for this email address.');
      }
    } catch (err) {
      setOrder(null);
      setError(
        err instanceof Error ? err.message : 'Unable to find your orders.'
      );
    } finally {
      setLoading(false);
    }
  };

  // ── render ─────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-8">

        {/* breadcrumb */}
        <div className="text-xs uppercase tracking-[0.2em] text-gray-500 flex flex-wrap gap-2">
          <span>Home</span>
          <span>&gt;</span>
          <span className="text-gray-900 font-semibold">Track Order</span>
        </div>

        <div className="grid gap-8 lg:grid-cols-2">

          {/* ── left: search form ── */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 space-y-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Track your orders</h1>
              <p className="text-sm text-gray-600">
                Enter the email address used when placing your order and
                we&apos;ll show all your orders with their latest status.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="e.g. you@example.com"
                  className="w-full rounded-xl border border-gray-300 px-4 py-2.5 text-sm text-black focus:ring-2 focus:ring-orange-400 focus:border-orange-400"
                  required
                />
              </div>

              {error && (
                <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-2">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full inline-flex items-center justify-center gap-2 bg-orange-500 text-white px-4 py-3 rounded-xl font-semibold hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Search className="h-4 w-4" />
                {loading ? 'Searching...' : 'Find my orders'}
              </button>
            </form>

            <div className="border border-dashed border-gray-300 rounded-2xl px-4 py-3 text-xs text-gray-500 space-y-1">
              <p>- Use the same email address you provided at checkout.</p>
              <p>- All orders linked to that email will be listed.</p>
            </div>
          </div>

          {/* ── right: results panel ── */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 space-y-6">
            {order ? (
              <>
                {/* order selector (multiple orders) */}
                {orders.length > 1 && (
                  <div className="space-y-2">
                    <p className="text-xs uppercase tracking-[0.3em] text-gray-500">
                      All orders for this email
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {orders.map((o) => (
                        <button
                          key={o.id}
                          type="button"
                          onClick={() => setOrder(o)}
                          className={`px-3 py-2 rounded-xl border text-xs text-left transition-colors ${
                            o.id === order.id
                              ? 'border-orange-500 bg-orange-50 text-orange-700'
                              : 'border-gray-200 bg-gray-50 text-gray-700 hover:border-orange-300 hover:bg-orange-50'
                          }`}
                        >
                          <div className="font-semibold">Order #{o.id}</div>
                          <div className="text-[11px] text-gray-500">
                            {orderDateFormatter(o.order_date)} ·{' '}
                            {formatCurrency(o.total_amount)}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* order summary */}
                <div className="space-y-2">
                  <p className="text-xs uppercase tracking-[0.3em] text-gray-500">Order summary</p>
                  <h2 className="text-xl font-semibold text-gray-900">Order #{order.id}</h2>
                  <p className="text-sm text-gray-600">
                    Placed on {orderDateFormatter(order.order_date)} –
                    <span className="ml-1 font-semibold text-orange-500">
                      {formatCurrency(order.total_amount)}
                    </span>
                  </p>
                </div>

                {/* status chips */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-2xl bg-orange-50 border border-orange-100 px-4 py-3">
                    <p className="text-xs uppercase tracking-wide text-orange-500">Tracking code</p>
                    <p className="text-sm font-semibold text-gray-900 break-all">
                      {order.tracking_code || 'Not assigned'}
                    </p>
                  </div>
                  <div className="rounded-2xl bg-gray-50 border border-gray-100 px-4 py-3">
                    <p className="text-xs uppercase tracking-wide text-gray-500">Status</p>
                    <p className="text-sm font-semibold text-gray-900 capitalize">
                      {order.status?.replace(/_/g, ' ') || 'pending'}
                    </p>
                  </div>
                </div>

                {/* meta */}
                <div className="space-y-4">
                  <div className="flex items-center gap-3 text-sm text-gray-700">
                    <Package className="h-4 w-4 text-orange-500" />
                    <span>
                      Payment:{' '}
                      <strong className="text-gray-900 capitalize">
                        {order.payment_status || 'pending'}
                      </strong>
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-gray-700">
                    <Truck className="h-4 w-4 text-orange-500" />
                    <span>
                      Delivery method:{' '}
                      <strong className="text-gray-900">Standard courier</strong>
                    </span>
                  </div>
                </div>

                {/* contact */}
                <div className="space-y-3 border-t border-gray-200 pt-4">
                  <div className="flex items-center gap-3 text-sm text-gray-700">
                    <Mail className="h-4 w-4 text-gray-400" />
                    <span className="break-all">{customerEmail}</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-gray-700">
                    <Phone className="h-4 w-4 text-gray-400" />
                    <span>{customerPhone}</span>
                  </div>
                </div>

                {/* items */}
                <div className="space-y-3 border-t border-gray-200 pt-4">
                  <p className="text-xs uppercase tracking-[0.2em] text-gray-500">Items</p>
                  <div className="space-y-3">
                    {orderItems.map((item, idx) => {
                      const specLabel = buildSpecLabel(item?.specification);
                      return (
                        <div
                          key={item.id ?? item.product ?? idx}
                          className="flex items-start justify-between text-sm"
                        >
                          <div>
                            <p className="font-semibold text-gray-900">
                              {item.product_name || item.product?.name || 'Product'}
                            </p>
                            <p className="text-xs text-gray-500">
                              Qty: {item.quantity || 1}
                              {specLabel && <span className="ml-2">{specLabel}</span>}
                            </p>
                          </div>
                          <p className="font-semibold text-gray-900">
                            {formatCurrency(parseAmount(item.price) * (item.quantity || 1))}
                          </p>
                        </div>
                      );
                    })}

                    <div className="flex items-center justify-between text-sm text-gray-600 pt-2 border-t border-dashed">
                      <span>Subtotal</span>
                      <span className="text-gray-900 font-semibold">{formatCurrency(subtotal)}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm text-gray-900 font-semibold">
                      <span>Total</span>
                      <span className="text-orange-500">{formatCurrency(total)}</span>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="h-full min-h-[320px] flex flex-col items-center justify-center text-center text-gray-500 space-y-3">
                <ClipboardList className="h-12 w-12 text-gray-300" />
                <p className="text-lg font-semibold text-gray-700">Enter your email to view orders</p>
                <p className="text-sm">
                  Once you submit your email, all linked orders and their latest status will appear here.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* support banner */}
        <div className="bg-white border border-gray-200 rounded-2xl px-6 py-5 flex flex-wrap items-center gap-6 shadow-sm">
          <div className="flex items-center gap-3 text-gray-700">
            <Truck className="h-6 w-6 text-orange-500" />
            <div>
              <p className="font-semibold text-gray-900">Need help?</p>
              <p className="text-sm text-gray-600">
                Our support team can assist with order changes or delivery issues.
              </p>
            </div>
          </div>
          <div className="ml-auto">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 text-sm font-semibold text-orange-500 hover:text-orange-600"
            >
              Contact support &rarr;
            </Link>
          </div>
        </div>

        {/* continue shopping */}
        <div className="flex justify-center">
          <Link
            to="/"
            className="inline-flex items-center justify-center gap-2 bg-orange-500 text-white px-8 py-3 rounded-lg font-semibold hover:bg-orange-600 transition-colors"
          >
            <ShoppingBag className="h-5 w-5" />
            Continue Shopping
          </Link>
        </div>
      </main>
    </div>
  );
}